import { i18next } from '@weavedev/lit-i18next';
import { TFunction } from 'i18next';
import HttpApi from 'i18next-http-backend';

import { getBaseUrl } from './utils.api';

declare global {
  interface Window {
    i18next: Promise<TFunction>;
  }
}

/**
 * Initializes the i18next library with the specified locale.
 *
 * This function sets up the i18next library to use the HttpApi backend for loading translations.
 * It configures the language settings, fallback language, and the path to load translation files.
 * Additionally, it logs errors during the initialization process in development mode.
 *
 * @param locale - The locale to initialize i18next with, formatted as a string.
 * @returns A promise that resolves to the i18next TFunction.
 */
export const i18nInit = (locale: string) => {
  const lng = locale?.replace('_', '-').toLowerCase();

  i18nextErrorLogger();

  return i18next.use(HttpApi).init(
    {
      lng,
      lowerCaseLng: true,
      fallbackLng: {
        default: ['en-us'],
      },
      load: 'currentOnly',
      debug: import.meta.env.MODE === 'develop' ? true : false,
      backend: {
        loadPath: `${
          import.meta.env.MODE === 'production' ? `${getBaseUrl()}/js` : ''
        }/translations/{{lng}}.json`,
      },
    },
    error => {
      if (error && import.meta.env.MODE === 'development') {
        console.error('Error while loading translations', error);
        return;
      }
    },
  );
};

/**
 * Throw errors based on some possible mistakes made in the i18next usage.
 */
const i18nextErrorLogger = () => {
  if (import.meta.env.MODE === 'production' && !getBaseUrl()) {
    throw new Error('getBaseUrl() must be defined in production mode');
  }
};

/**
 * Returns country code from locale
 */
export const getCountryCode = (locale: string) => {
  return locale.substring(0, 2) + '-' + locale.substring(3, 5);
};
